import styled from "@emotion/styled";
import type { FC } from "react";
import { RequirementsChecklist } from "@9amhealth/shared";
import translate from "src/lib/translate";
import type { RefillRequirement } from "src/state/RefillRequirementsCubit/RefillRequirementsTypes";
import {
  isRefillCheckInActionable,
  isRequirementLocked
} from "src/state/RefillRequirementsCubit/RefillRequirementsHelpers";

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Card = styled.div`
  background: var(--color-white);
  border-radius: 8px;
  box-shadow: var(--light-shadow);
  overflow: hidden;
`;

// Matches the caption below the pharmacy tile (PharmacyInformationDetail: color-c-80): the
// charcoal-80 color and 16px left padding align it with the card content above.
const Hint = styled.p`
  margin: 0;
  padding-left: 1rem;
  font-size: 0.8125rem;
  color: var(--color-charcoal-80);
  line-height: 1.4;
`;

/**
 * The padlock disclaimer only makes sense beside a visible padlock, so it follows the requirements
 * rather than the renewal window — a pre-window member with nothing locked is not told about locks
 * they cannot see. Locked and overdue cannot co-occur (the backend locks requirements only before
 * the window opens), so their order here is a tie-break that should never fire.
 */
const HINT_KEYS = {
  locked: "prepareRefillDialog.v2.requirements.lockedHint",
  overdue: "prepareRefillDialog.v2.requirements.overdueHint",
  home: "prepareRefillDialog.v2.requirements.homeHint"
} as const;

const getHintKey = (hasLockedRow: boolean, overdue: boolean) => {
  if (hasLockedRow) {
    return HINT_KEYS.locked;
  }
  if (overdue) {
    return HINT_KEYS.overdue;
  }
  return HINT_KEYS.home;
};

/**
 * Requirements checklist: a single card of divided rows + a hint below it. Rows are
 * display-only except the outstanding refill check-in, which opens the refill survey via
 * `onOpenSurvey`.
 */
const RefillRequirementsChecklist: FC<{
  requirements: RefillRequirement[];
  /** Renewal status — gates the check-in row so it only opens the survey at PENDING_SURVEY. */
  status?: string;
  /** Past the renewal deadline — swaps the hint for the urgency copy (FEWEB-2710). */
  overdue?: boolean;
  onOpenSurvey?: () => void;
}> = ({ requirements, status, overdue = false, onOpenSurvey }) => {
  if (requirements.length === 0) {
    return null;
  }

  const hintKey = getHintKey(requirements.some(isRequirementLocked), overdue);

  return (
    <Section>
      <Card>
        <RequirementsChecklist
          requirements={requirements.map((requirement) => ({
            ...requirement,
            locked: isRequirementLocked(requirement)
          }))}
          getRowAction={(requirement) =>
            onOpenSurvey && isRefillCheckInActionable(requirement, status)
              ? onOpenSurvey
              : undefined
          }
        />
      </Card>
      <Hint>{translate(hintKey)}</Hint>
    </Section>
  );
};

export default RefillRequirementsChecklist;
